import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  RequestConfig,
  APIMethod,
  GraphQLConfig,
  MCPTool,
  NormalizedParameters,
  ActionTestResponse,
} from '@/types/mcpTool';
import { MCPToolsService } from '@/api/services/mcpToolsService';
import {
  Play,
  Loader2,
  CheckCircle,
  AlertCircle,
  Clock,
  Code,
  FileJson,
  Zap,
} from 'lucide-react';

interface ActionTestSimulatorProps {
  tool: MCPTool;
  actionName: string;
  endpoint: string;
  method: APIMethod;
  parameters: NormalizedParameters;
  graphqlConfig?: GraphQLConfig;
  requestConfig?: RequestConfig;
}

/**
 * ActionTestSimulator Component
 *
 * Live test execution for actions before saving
 * Features:
 * - Execute actual API calls
 * - Display formatted response
 * - Show execution time and status code
 * - Display projected data if configured
 * - Error handling
 */
export const ActionTestSimulator: React.FC<ActionTestSimulatorProps> = ({
  tool,
  actionName,
  endpoint,
  method,
  parameters,
  graphqlConfig,
  requestConfig,
}) => {
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState<ActionTestResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleExecuteTest = async () => {
    setLoading(true);
    setError(null);
    setTestResult(null);

    try {
      const testRequest = {
        parameters,
        graphql_config: graphqlConfig,
      };

      const response = await MCPToolsService.testAction(
        tool.id!,
        {action_name:actionName,endpoint,http_method:method === 'GRAPHQL' ? 'POST' : method,graphql_config:graphqlConfig,request_config:requestConfig},
        testRequest
      );

      setTestResult(response);

      if (!response.success && response.error) {
        setError(response.error);
      }
    } catch (err: any) {
      console.error('Test execution failed:', err);
      setError(err.message || 'Failed to execute test');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = () => {
    if (!testResult) return null;

    if (testResult.status_code >= 200 && testResult.status_code < 300) {
      return (
        <Badge className="bg-green-100 text-green-700">
          <CheckCircle className="h-3 w-3 mr-1" />
          {testResult.status_code} OK
        </Badge>
      );
    } else if (testResult.status_code >= 400 && testResult.status_code < 500) {
      return (
        <Badge className="bg-orange-100 text-orange-700">
          <AlertCircle className="h-3 w-3 mr-1" />
          {testResult.status_code} Client Error
        </Badge>
      );
    } else if (testResult.status_code >= 500) {
      return (
        <Badge className="bg-red-100 text-red-700">
          <AlertCircle className="h-3 w-3 mr-1" />
          {testResult.status_code} Server Error
        </Badge>
      );
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Zap className="h-5 w-5 text-yellow-600" />
            Test Action Execution
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Execute this action with live parameters before saving
          </p>
        </div>
        <Button
          onClick={handleExecuteTest}
          disabled={loading}
          size="lg"
          className="bg-green-600 hover:bg-green-700"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Executing...
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-2" />
              Execute Test
            </>
          )}
        </Button>
      </div>

      {/* Test Configuration Preview */}
      <Card className="p-4 bg-gray-50">
        <h4 className="text-sm font-medium mb-3">Test Configuration:</h4>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-gray-600 w-24">Tool:</span>
            <Badge variant="outline">{tool.name}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600 w-24">Endpoint:</span>
            <code className="text-xs bg-white px-2 py-1 rounded border">
              {tool.base_url}{endpoint}
            </code>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600 w-24">Method:</span>
            <Badge variant="secondary">{method}</Badge>
          </div>

          {Object.keys(parameters.path_params).length > 0 && (
            <div className="flex gap-2">
              <span className="text-gray-600 w-24">Path Params:</span>
              <code className="text-xs bg-white px-2 py-1 rounded border flex-1">
                {JSON.stringify(parameters.path_params)}
              </code>
            </div>
          )}

          {Object.keys(parameters.query_params).length > 0 && (
            <div className="flex gap-2">
              <span className="text-gray-600 w-24">Query Params:</span>
              <code className="text-xs bg-white px-2 py-1 rounded border flex-1">
                {JSON.stringify(parameters.query_params)}
              </code>
            </div>
          )}

          {parameters.body && (
            <div className="flex gap-2">
              <span className="text-gray-600 w-24">Body:</span>
              <pre className="text-xs bg-white px-2 py-1 rounded border flex-1 overflow-x-auto">
                {JSON.stringify(parameters.body, null, 2)}
              </pre>
            </div>
          )}

          {graphqlConfig && (
            <div className="flex gap-2">
              <span className="text-gray-600 w-24">GraphQL:</span>
              <pre className="text-xs bg-white px-2 py-1 rounded border flex-1 overflow-x-auto">
                {graphqlConfig.query}
              </pre>
            </div>
          )}
        </div>
      </Card>

      <Separator />

      {/* Error Display */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Test Result */}
      {testResult && (
        <div className="space-y-4">
          {/* Status Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h4 className="text-sm font-semibold">Test Result:</h4>
              {getStatusBadge()}
              <Badge variant="outline" className="text-xs">
                <Clock className="h-3 w-3 mr-1" />
                {testResult.execution_time_ms}ms
              </Badge>
            </div>
          </div>

          {/* Response Tabs */}
          <Tabs defaultValue="formatted" className="w-full">
            <TabsList>
              <TabsTrigger value="formatted">
                <FileJson className="h-4 w-4 mr-2" />
                Formatted
              </TabsTrigger>
              <TabsTrigger value="raw">
                <Code className="h-4 w-4 mr-2" />
                Raw JSON
              </TabsTrigger>
              {testResult.projected_data && (
                <TabsTrigger value="projected">
                  <Zap className="h-4 w-4 mr-2" />
                  Projected Data
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="formatted" className="space-y-2">
              <Card className="p-4 bg-gray-50">
                <pre className="text-sm text-gray-800 overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(testResult.response, null, 2)}
                </pre>
              </Card>
            </TabsContent>

            <TabsContent value="raw" className="space-y-2">
              <Card className="p-4 bg-gray-900">
                <pre className="text-sm text-green-400 font-mono overflow-x-auto">
                  {JSON.stringify(testResult.response, null, 2)}
                </pre>
              </Card>
            </TabsContent>

            {testResult.projected_data && (
              <TabsContent value="projected" className="space-y-2">
                <Alert className="border-blue-200 bg-blue-50">
                  <FileJson className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-900">
                    This is how the data will be formatted after applying response projection
                  </AlertDescription>
                </Alert>
                <Card className="p-4 bg-gray-50">
                  <pre className="text-sm text-gray-800 overflow-x-auto whitespace-pre-wrap">
                    {JSON.stringify(testResult.projected_data, null, 2)}
                  </pre>
                </Card>
              </TabsContent>
            )}
          </Tabs>

          {/* Response Headers (if available) */}
          {testResult.headers && Object.keys(testResult.headers).length > 0 && (
            <details className="group">
              <summary className="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-900 flex items-center gap-2">
                <span className="transform transition-transform group-open:rotate-90">▶</span>
                Response Headers
              </summary>
              <Card className="p-3 mt-2 bg-gray-50">
                <div className="space-y-1">
                  {Object.entries(testResult.headers).map(([key, value]) => (
                    <div key={key} className="flex gap-2 text-xs">
                      <span className="font-medium text-gray-600">{key}:</span>
                      <span className="text-gray-800">{value}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </details>
          )}
        </div>
      )}

      {/* Help Text */}
      {!testResult && !loading && (
        <Alert className="border-blue-200 bg-blue-50">
          <Zap className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-900">
            <strong>Note:</strong> This will execute a real API call to {tool.base_url}.
            Make sure your test parameters won't cause unintended side effects.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
